import { forwardRef } from 'react';
import type {
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

type ControlProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>;

/**
 * Every control forwards its ref.
 *
 * This is not a convenience. react-hook-form registers a field through the ref
 * it hands the control; a component that swallows it is never registered, so
 * its value is always empty and `handleSubmit` silently refuses to run. A
 * control that cannot be registered is a control that cannot be in a form.
 */
export const TextInput = forwardRef<HTMLInputElement, ControlProps>(function TextInput(props, ref) {
  return <input {...props} ref={ref} className="control" />;
});

/**
 * Money is entered and displayed in tabular monospace, right-aligned, so a
 * column of amounts aligns on the decimal (§3).
 */
export const MoneyInput = forwardRef<HTMLInputElement, ControlProps & { currency?: string }>(
  function MoneyInput({ currency, ...rest }, ref) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        {currency === undefined ? null : (
          <span style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-sm)' }}>{currency}</span>
        )}
        <input {...rest} ref={ref} inputMode="decimal" className="control control--numeric" />
      </div>
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>
>(function Textarea(props, ref) {
  return <textarea {...props} ref={ref} className="control control--textarea" />;
});

export interface SelectOption {
  value: string;
  label: string;
}

export const Select = forwardRef<
  HTMLSelectElement,
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & {
    options: SelectOption[];
    placeholder?: string;
  }
>(function Select({ options, placeholder, ...rest }, ref) {
  return (
    <select {...rest} ref={ref} className="control">
      {placeholder === undefined ? null : <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
});

export function Checkbox({ label, ...rest }: ControlProps & { label: ReactNode }): ReactElement {
  return (
    <label className="choice">
      <input {...rest} type="checkbox" />
      <span className="choice__label">{label}</span>
    </label>
  );
}

export interface RadioOption {
  value: string;
  label: string;
}

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  legend,
}: {
  name: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  legend: string;
}): ReactElement {
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
      <legend className="field__label">{legend}</legend>
      {options.map((option) => (
        <label className="choice" key={option.value}>
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange?.(option.value)}
          />
          <span className="choice__label">{option.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

/** Only for settings that take effect immediately — never as a form field. */
export function Switch({
  label,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}): ReactElement {
  return (
    <label className="choice">
      <span className="switch">
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-label={label}
          onChange={(event) => onCheckedChange(event.target.checked)}
        />
        <span className="switch__track" />
        <span className="switch__thumb" />
      </span>
      <span className="choice__label">{label}</span>
    </label>
  );
}

export function DateRangeField({
  fromProps,
  toProps,
}: {
  fromProps: ControlProps;
  toProps: ControlProps;
}): ReactElement {
  return (
    <div className="date-range">
      <input {...fromProps} type="date" className="control" aria-label="From" />
      <span className="date-range__sep" aria-hidden="true">
        →
      </span>
      <input {...toProps} type="date" className="control" aria-label="To" />
    </div>
  );
}
